import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Mail, MapPin, Send, Instagram, Clock, CheckCircle2, ArrowUpRight, Loader2, Plus, Lock as LockIcon, MessageCircle, ChevronLeft, ChevronRight, Handshake } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import type { UIKey } from "@/i18n/ui";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { listMyTickets } from "@/lib/support.functions";
import { submitContactMessage } from "@/lib/contact.functions";
import { SupportChat } from "@/components/SupportChat";
import { useHideGlobalMapFab } from "@/lib/map-fab-visibility";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact — what Finland Oy" },
      { name: "description", content: "Get in touch with what Finland Oy — partnerships, press, support and general inquiries." },
      { property: "og:title", content: "Contact — what Finland Oy" },
      { property: "og:description", content: "Reach out for partnerships, press or general inquiries." },
      { property: "og:url", content: "https://whatfinland.com/contact" },
    ],
    links: [
      { rel: "canonical", href: "https://whatfinland.com/contact" },
    ],
  }),
  component: ContactPage,
});

const SUBJECTS = [
  { value: "general",     labelKey: "contact.subject.general"     as UIKey, to: "info@whatfinland.com" },
  { value: "partnership", labelKey: "contact.subject.partnership" as UIKey, to: "partnerships@whatfinland.com" },
  { value: "media",       labelKey: "contact.subject.media"       as UIKey, to: "partnerships@whatfinland.com" },
  { value: "support",     labelKey: "contact.subject.support"     as UIKey, to: "info@whatfinland.com" },
] as const;

type SubjectValue = (typeof SUBJECTS)[number]["value"];
type FormState = { name: string; company: string; email: string; phone: string; subject: SubjectValue; message: string };

const initial: FormState = { name: "", company: "", email: "", phone: "", subject: "general", message: "" };

function validate(f: FormState, t: (k: UIKey) => string) {
  const errors: Partial<Record<keyof FormState, string>> = {};
  if (!f.name.trim()) errors.name = t("contact.error.name");
  else if (f.name.length > 100) errors.name = t("contact.error.name.long");
  if (!f.email.trim()) errors.email = t("contact.error.email.required");
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errors.email = t("contact.error.email.invalid");
  else if (f.email.length > 255) errors.email = t("contact.error.email.long");
  if (f.company.length > 150) errors.company = t("contact.error.company.long");
  if (f.phone && f.phone.length > 40) errors.phone = t("contact.error.phone.long");
  if (!f.message.trim()) errors.message = t("contact.error.message.required");
  else if (f.message.length > 2000) errors.message = t("contact.error.message.long");
  return errors;
}

const INTER = '"Inter Variable", Inter, ui-sans-serif, system-ui, sans-serif';
const inter = { fontFamily: INTER, fontStyle: "normal" as const };

function ContactPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const qc = useQueryClient();
  useHideGlobalMapFab();
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null);
  const [forceNewTicket, setForceNewTicket] = useState(false);
  const [justSentChat, setJustSentChat] = useState(false);
  const successRef = useRef<HTMLDivElement | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const formStartedAtRef = useRef<number>(Date.now());

  // Load this user's existing tickets so first-timers see the form,
  // returning users see the chat by default.
  const listFn = useServerFn(listMyTickets);
  const { data: myTicketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ["my-tickets", user?.id],
    queryFn: () => listFn(),
    enabled: !!user?.id,
  });
  const ticketCount = myTicketsData?.tickets?.length ?? 0;
  // Signed-in users go straight to the conversations inbox (even with 0 tickets);
  // anonymous users see the contact form.
  const showChat = !!user?.id && (justSentChat || !forceNewTicket);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submitFn = useServerFn(submitContactMessage);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const errs = validate(form, t);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please fix the highlighted fields and try again.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitFn({
        data: {
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          phone: form.phone.trim(),
          subject: form.subject,
          message: form.message.trim(),
          website: honeypot,
          formStartedAt: formStartedAtRef.current,
        },
      });
      const emailSent = form.email.trim();
      setSentEmail(emailSent);
      setSent(true);
      setForm(initial);
      toast.success("Message sent — we'll reply within 48h.");
      if (user?.id && result?.id) {
        setActiveTicketId(result.id);
        setJustSentChat(true);
        setForceNewTicket(false);
        qc.invalidateQueries({ queryKey: ["my-tickets", user.id] });
      } else {
        setTimeout(() => {
          successRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 60);
      }
    } catch (err: any) {
      const msg = err?.message ?? "Could not send. Please try again.";
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Logged-in user with at least one ticket (or who just sent one) → chat view by default.
  if (showChat) {
    return (
      <div style={inter} className="min-h-screen bg-[#eef1f6] md:py-8 md:px-6 lg:py-10">
        <div className="max-w-5xl mx-auto md:px-0">
          <InlineTicketsChat
            userId={user!.id}
            activeTicketId={activeTicketId}
            onSelect={setActiveTicketId}
            onNewTicket={() => { setForceNewTicket(true); setJustSentChat(false); setSent(false); setActiveTicketId(null); }}
          />
        </div>
      </div>
    );
  }

  // Avoid flashing the form before we know if this user already has tickets.
  if (user?.id && ticketsLoading) {
    return (
      <div style={inter} className="min-h-[60vh] grid place-items-center text-navy/50 text-sm">
        <Loader2 className="size-5 animate-spin text-navy/40" />
      </div>
    );
  }


  // If the user already has tickets and is currently forcing a new ticket,
  // show a "back to my tickets" link so they can return to the list.
  const hasExistingTickets = !!user?.id && ticketCount > 0;

  return (
    <div style={inter} className="min-h-screen bg-[#eef1f6] py-5 px-4 sm:px-6 lg:py-10">
      <div className="max-w-6xl mx-auto">
        {hasExistingTickets && (
          <div className="mb-3 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => { setForceNewTicket(false); setJustSentChat(false); setSent(false); }}
              className="inline-flex h-8 items-center gap-1.5 text-[12px] font-semibold text-navy/70 hover:text-navy bg-white ring-1 ring-navy/10 px-3 rounded-full transition-colors normal-case">
              <ChevronLeft className="size-3.5" /> My tickets
            </button>
            <span className="text-[11px] font-semibold text-navy/45 normal-case">
              New ticket
            </span>
          </div>
        )}
        <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-3 lg:gap-0 bg-white rounded-2xl overflow-hidden ring-1 ring-navy/10 shadow-[0_16px_40px_-24px_rgba(15,30,60,0.28)]">
          <aside className="relative bg-[#0b1d3a] text-white p-5 sm:p-7 lg:p-8 overflow-hidden">
            <div className="absolute -top-24 -right-24 size-64 rounded-full bg-teal/20 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col h-full min-h-0">
              <div className="flex items-center justify-between mb-5">
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/50">
                  what Finland Oy
                </span>
                <span className="inline-flex h-6 items-center rounded-full bg-white/10 px-2 text-[11px] font-semibold text-white/80">48h reply</span>
              </div>

              <h1 className="text-[26px] sm:text-[32px] font-semibold leading-[1.1] tracking-tight mb-2 not-italic normal-case">
                Let’s talk
              </h1>
              <p className="text-white/65 text-[13px] leading-relaxed mb-6 max-w-sm">
                Partnerships, press, or a place we should know. We reply on business days.
              </p>

              <div className="space-y-1 mb-auto">
                <InfoRow icon={<Mail className="size-3.5" />} label="General" value="info@whatfinland.com" href="mailto:info@whatfinland.com" />
                <InfoRow icon={<Handshake className="size-3.5" />} label="Partnerships" value="partnerships@whatfinland.com" href="mailto:partnerships@whatfinland.com" />
                <InfoRow icon={<MapPin className="size-3.5" />} label="Based in" value="Helsinki, Finland" />
                <InfoRow icon={<Clock className="size-3.5" />} label="Hours" value="Mon–Fri · 09–17 EET" />
              </div>

              <div className="pt-5 mt-5 border-t border-white/10 flex items-center gap-3">
                <span className="text-[11px] font-semibold text-white/50">Follow</span>
                <a href="https://instagram.com/whatfinland" target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white/85 hover:text-white transition-colors">
                  <Instagram className="size-3.5" /> @whatfinland
                </a>
              </div>
            </div>
          </aside>

          <form onSubmit={submit} noValidate className="p-5 sm:p-7 lg:p-8">
            {/* Honeypot — hidden from real users, bots will fill it */}
            <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden">
              <label>
                Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </label>
            </div>
            <div className="flex items-start justify-between gap-3 mb-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/45 mb-1">
                  Send a message
                </p>
                <h2 className="text-[18px] sm:text-[20px] font-semibold text-navy tracking-tight not-italic normal-case">
                  What’s on your mind?
                </h2>
              </div>
              <span className="hidden sm:inline-flex h-7 items-center gap-1.5 text-[11px] font-semibold text-navy/55 bg-navy/[0.05] px-2.5 rounded-full">
                <span className="size-1.5 rounded-full bg-teal" /> 48h reply
              </span>
            </div>

            <div className="mb-3">
              <Field label={t("contact.field.subject") ?? "Subject"} required>
                <select
                  value={form.subject}
                  onChange={(e) => update("subject", e.target.value as SubjectValue)}
                  className={inputCls}
                  required
                >
                  {SUBJECTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {t(s.labelKey)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={t("contact.field.name")} required error={errors.name}>
                <input type="text" value={form.name} onChange={(e) => update("name", e.target.value)}
                  maxLength={100} required autoComplete="name"
                  placeholder={t("contact.field.name.placeholder")} className={inputCls} />
              </Field>
              <Field label={t("contact.field.email")} required error={errors.email}>
                <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                  maxLength={255} required autoComplete="email"
                  placeholder="you@example.com" className={inputCls} />
              </Field>
              <Field label={t("contact.field.company")} error={errors.company}>
                <input type="text" value={form.company} onChange={(e) => update("company", e.target.value)}
                  maxLength={150} autoComplete="organization"
                  placeholder={t("contact.field.optional")} className={inputCls} />
              </Field>
              <Field label={t("contact.field.phone")} error={errors.phone}>
                <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)}
                  maxLength={40} autoComplete="tel"
                  placeholder={t("contact.field.optional")} className={inputCls} />
              </Field>
            </div>

            <div className="mt-3">
              <Field label={t("contact.field.message")} required error={errors.message}>
                <textarea value={form.message} onChange={(e) => update("message", e.target.value)}
                  maxLength={2000} required rows={5}
                  placeholder={t("contact.field.message.placeholder")}
                  className={`${areaCls} resize-y`} />
                <p className="text-[11px] text-navy/40 mt-1 text-right tabular-nums">
                  {form.message.length} / 2000
                </p>
              </Field>
            </div>

            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-navy/10">
              <p className="text-[11px] text-navy/50 leading-snug">
                {t("contact.agree")}
              </p>
              <button type="submit" disabled={submitting}
                className="inline-flex h-11 sm:h-10 w-full sm:w-auto items-center justify-center gap-1.5 bg-[#0b1d3a] text-white px-5 rounded-full text-[13px] font-semibold hover:bg-royal transition-colors disabled:opacity-60 disabled:cursor-not-allowed normal-case">
                {submitting ? "Sending…" : t("contact.submit")}
                {submitting ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
              </button>
            </div>

            <div className="mt-4 flex justify-center sm:justify-start">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-navy/60 hover:text-navy transition-colors normal-case"
              >
                <ChevronLeft className="size-3.5" />
                Back to home
              </Link>
            </div>

            {submitError && (
              <div ref={successRef} className="mt-4 bg-rose-50 border border-rose-200 rounded-xl p-3 text-[13px] text-rose-700">
                {submitError}
              </div>
            )}

            {sent && (
              <div ref={successRef} className="mt-4 bg-teal/10 border border-teal/30 rounded-xl p-3.5 flex items-start gap-2.5">
                <CheckCircle2 className="size-4 text-teal shrink-0 mt-0.5" />
                <p className="text-[13px] text-navy leading-snug">
                  Thanks — we’ll reply to <span className="font-semibold">{sentEmail || "your email"}</span> within 48h.{" "}
                  <Link to="/login" className="font-semibold underline">Sign in</Link> to chat with support.
                </p>
              </div>
            )}
            <div className="h-20 sm:h-0" aria-hidden="true" />
          </form>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full h-10 bg-slate-50 border border-navy/10 rounded-xl px-3 text-[13px] sm:text-sm text-navy placeholder:text-navy/35 not-italic normal-case focus:outline-none focus:border-navy/30 focus:bg-white focus:ring-2 focus:ring-navy/10";
const areaCls =
  "w-full min-h-[8rem] bg-slate-50 border border-navy/10 rounded-xl px-3 py-2.5 text-[13px] sm:text-sm text-navy placeholder:text-navy/35 not-italic normal-case focus:outline-none focus:border-navy/30 focus:bg-white focus:ring-2 focus:ring-navy/10";

function Field({
  label, required, error, children,
}: { label: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-navy/55 block mb-1 normal-case">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      {children}
      {error && <span className="text-[11px] text-rose-600 mt-1 block">{error}</span>}
    </label>
  );
}

function InfoRow({
  icon, label, value, href,
}: { icon: React.ReactNode; label: string; value: string; href?: string }) {
  const content = (
    <div className="flex items-center gap-3 py-2 border-b border-white/10 last:border-0 group">
      <span className="size-8 rounded-full bg-white/10 text-white flex items-center justify-center shrink-0 group-hover:bg-white/15 transition-colors">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.14em] text-white/45 font-semibold">{label}</p>
        <p className="text-[13px] text-white font-medium truncate">{value}</p>
      </div>
      {href && <ArrowUpRight className="size-3.5 text-white/30 group-hover:text-white transition-colors" />}
    </div>
  );
  return href ? <a href={href} className="block">{content}</a> : content;
}

/* ============ Inline tickets + chat (post-submit view) ============
 * List ↔ detail flow:
 *  - Default = full ticket list with open/closed stats at top
 *  - Click a ticket → opens chat view (with back button)
 *  - Closed tickets show a lock badge in list + a banner in chat
 */
function InlineTicketsChat({
  userId, activeTicketId, onSelect, onNewTicket,
}: { userId: string; activeTicketId: string | null; onSelect: (id: string) => void; onNewTicket: () => void }) {
  const listFn = useServerFn(listMyTickets);
  const { data, isLoading } = useQuery({
    queryKey: ["my-tickets", userId],
    queryFn: () => listFn(),
  });
  const tickets = (data?.tickets ?? []) as Array<{ id: string; subject: string; message: string; status: string; created_at: string }>;
  const selected = tickets.find((t) => t.id === activeTicketId) ?? null;

  const openCount = tickets.filter((t) => t.status !== "closed").length;
  const closedCount = tickets.filter((t) => t.status === "closed").length;

  if (isLoading) {
    return (
      <div className="px-4 py-10 md:p-10 grid place-items-center text-navy/50 text-sm">
        <Loader2 className="size-5 animate-spin mb-2 text-teal" /> loading…
      </div>
    );
  }

  // -------- DETAIL VIEW (chat open) --------
  if (selected) {
    const closed = selected.status === "closed";
    return (
      <div className="flex flex-col h-[100svh] md:h-auto md:rounded-3xl md:bg-white md:border md:border-navy/10 md:shadow-[0_30px_80px_-30px_rgba(15,30,60,0.18)] md:overflow-hidden">
        {/* mobile back to profile */}
        <div className="md:hidden px-3 pt-[calc(env(safe-area-inset-top)+12px)] pb-2 bg-white">
          <Link
            to="/profile"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white ring-1 ring-navy/10 text-navy text-[11px] font-bold uppercase tracking-[0.18em] hover:bg-navy/5 transition-colors"
          >
            <ChevronLeft className="size-3.5" strokeWidth={2.5} />
            <span>back</span>
          </Link>
        </div>
        {/* sticky chat header */}
        <header className="flex items-center gap-2 px-3 md:px-5 py-3 md:py-4 border-b border-navy/10 bg-white sticky top-0 z-30 md:pt-4">
          <button
            type="button"
            onClick={() => onSelect("")}
            aria-label="back to tickets"
            className="shrink-0 size-9 rounded-full bg-navy/5 hover:bg-navy/10 text-navy grid place-items-center transition-colors">
            <ChevronLeft className="size-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-navy/50">ticket</span>
              <span
                className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                  closed ? "bg-navy/10 text-navy/60"
                  : selected.status === "replied" ? "bg-emerald-100 text-emerald-700"
                  : "bg-teal/15 text-navy"
                }`}>
                {closed && <LockIcon className="size-2.5 inline mr-0.5 -mt-0.5" />}{selected.status}
              </span>
            </div>
            <h2 className="text-[15px] md:text-base font-bold text-navy tracking-tight truncate leading-tight">
              {selected.subject || "general"}
            </h2>
          </div>
          <button
            type="button"
            onClick={onNewTicket}
            className="shrink-0 inline-flex items-center gap-1.5 bg-navy text-white px-3 py-2 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] hover:bg-teal transition-colors">
            <Plus className="size-3.5" /> new
          </button>
        </header>

        {/* closed banner */}
        {closed && (
          <div className="mx-3 md:mx-5 mt-3 rounded-xl bg-navy/[0.04] border border-navy/10 px-3 py-2.5 flex items-start gap-2">
            <LockIcon className="size-3.5 text-navy/60 mt-0.5 shrink-0" />
            <p className="text-[11px] text-navy/70 leading-snug">
              this ticket is closed. open a new ticket if you need more help.
            </p>
          </div>
        )}

        {/* chat */}
        <div className="flex-1 min-h-0 p-2 md:p-4">
          <SupportChat
            ticketId={selected.id}
            viewerRole="user"
            isClosed={closed}
            currentUserId={userId}
          />
        </div>
      </div>
    );
  }

  // -------- LIST VIEW --------
  const replyingCount = tickets.filter((t) => t.status === "replied").length;
  return (
    <div className="flex flex-col min-h-[100svh] md:min-h-0">
      {/* hero header card */}
      <div className="px-4 md:px-0 pt-[calc(env(safe-area-inset-top)+16px)] md:pt-0 mb-5 md:mb-7">
        <Link
          to="/profile"
          className="md:hidden inline-flex items-center gap-1.5 mb-3 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm ring-1 ring-navy/10 text-navy text-[11px] font-bold uppercase tracking-[0.18em] hover:bg-white transition-colors"
        >
          <ChevronLeft className="size-3.5" strokeWidth={2.5} />
          <span>back</span>
        </Link>

        <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-navy via-[#0d2750] to-[#081a36] text-white px-5 md:px-8 py-6 md:py-8 shadow-[0_30px_80px_-30px_rgba(8,26,54,0.55)] ring-1 ring-white/5">
            {/* decorative layers */}
          <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
              maskImage: "radial-gradient(ellipse at top right, black 30%, transparent 70%)",
            }}
          />
          <div className="absolute -top-24 -right-20 size-72 rounded-full bg-teal/35 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-28 -left-20 size-72 rounded-full bg-[#1e4a8a]/50 blur-3xl pointer-events-none" />
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 mb-3 px-2.5 py-1 rounded-full bg-white/10 ring-1 ring-white/15 backdrop-blur-sm">
                <span className="relative flex size-1.5">
                  <span className="absolute inset-0 rounded-full bg-teal animate-ping opacity-75" />
                  <span className="relative size-1.5 rounded-full bg-teal" />
                </span>
                <p className="text-[9px] md:text-[10px] uppercase tracking-[0.28em] font-semibold text-teal">support inbox</p>
              </div>
              <h1 className="text-[22px] md:text-[28px] font-semibold tracking-tight leading-[1.1] not-italic normal-case">
                Your conversations
              </h1>
              <p className="text-white/60 text-[12px] md:text-sm mt-2.5 max-w-md leading-relaxed">
                we reply within 48h, Mon–Fri. tap any ticket to continue the chat.
              </p>
            </div>
            <button
              type="button"
              onClick={onNewTicket}
              className="group shrink-0 self-start md:self-auto inline-flex items-center gap-2 bg-white text-navy pl-4 pr-2 py-2 rounded-full text-[11px] md:text-[12px] font-bold uppercase tracking-[0.18em] hover:bg-teal hover:text-white transition-all shadow-[0_10px_30px_-10px_rgba(255,255,255,0.5)] hover:shadow-[0_10px_30px_-10px_rgba(20,184,166,0.6)]">
              <span>new ticket</span>
              <span className="size-7 rounded-full bg-navy text-white grid place-items-center group-hover:bg-white group-hover:text-teal transition-colors">
                <Plus className="size-3.5" strokeWidth={2.5} />
              </span>
            </button>
          </div>

          {/* inline stats */}
          <div className="relative grid grid-cols-3 gap-2 md:gap-3 mt-6 md:mt-7 pt-5 border-t border-white/10">
            <HeroStat label="open" value={openCount} accent />
            <HeroStat label="replied" value={replyingCount} />
            <HeroStat label="closed" value={closedCount} dim />
          </div>
        </div>
      </div>

      {/* list section label */}
      {tickets.length > 0 && (
        <div className="px-4 md:px-0 mb-2.5 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-navy/55">all tickets</p>
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-navy/40 tabular-nums">{tickets.length} total</p>
        </div>
      )}

      {/* list */}
      {tickets.length === 0 ? (
        <div className="mx-4 md:mx-0 rounded-3xl bg-white border border-navy/10 px-6 py-14 text-center shadow-[0_20px_50px_-30px_rgba(15,30,60,0.15)]">
          <div className="size-16 rounded-2xl bg-teal/10 grid place-items-center mx-auto mb-4">
            <MessageCircle className="size-7 text-teal" />
          </div>
          <p className="text-navy font-bold text-base mb-1">no tickets yet</p>
          <p className="text-navy/55 text-xs leading-relaxed max-w-[16rem] mx-auto">
            start a conversation with our team — we usually reply within a day.
          </p>
          <button
            type="button"
            onClick={onNewTicket}
            className="inline-flex items-center gap-1.5 bg-navy text-white px-5 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-teal transition-colors mt-5">
            <Plus className="size-3.5" /> new ticket
          </button>
        </div>
      ) : (
        <div className="mx-4 md:mx-0 rounded-3xl bg-white border border-navy/10 overflow-hidden divide-y divide-navy/[0.06] shadow-[0_20px_50px_-30px_rgba(15,30,60,0.15)]">
          {tickets.map((t) => {
            const closed = t.status === "closed";
            const isReplied = t.status === "replied";
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onSelect(t.id)}
                className="group w-full text-left px-4 md:px-5 py-4 hover:bg-[hsl(190_40%_98%)] active:bg-navy/[0.04] transition-colors flex items-center gap-3.5">
                <span className={`relative shrink-0 size-11 rounded-2xl grid place-items-center transition-transform group-hover:scale-105 ${
                  closed
                    ? "bg-navy/[0.06] text-navy/40"
                    : "bg-gradient-to-br from-teal/25 to-teal/10 text-teal ring-1 ring-teal/20"
                }`}>
                  {closed ? <LockIcon className="size-4" /> : <MessageCircle className="size-4" />}
                  {isReplied && (
                    <span className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-bold text-sm text-navy truncate">{t.subject || "general"}</span>
                    <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      closed ? "bg-navy/10 text-navy/60"
                      : isReplied ? "bg-emerald-100 text-emerald-700"
                      : "bg-teal/15 text-navy"
                    }`}>
                      {t.status}
                    </span>
                  </div>
                  <p className="text-[12.5px] text-navy/60 truncate leading-snug">{t.message}</p>
                  <p className="text-[10px] text-navy/40 mt-1 font-medium tabular-nums">
                    {new Date(t.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                </div>
                <ChevronRight className="shrink-0 size-4 text-navy/25 group-hover:text-navy/60 group-hover:translate-x-0.5 transition-all" />
              </button>
            );
          })}
        </div>
      )}
      <div className="h-8 md:h-0" aria-hidden="true" />
    </div>
  );
}

function HeroStat({ label, value, accent, dim }: { label: string; value: number; accent?: boolean; dim?: boolean }) {
  const tone = accent
    ? "bg-teal/15 ring-teal/30"
    : dim
    ? "bg-white/[0.03] ring-white/5"
    : "bg-white/[0.06] ring-white/10";
  const labelTone = accent ? "text-teal" : "text-white/75";
  const valueTone = dim ? "text-white/70" : "text-white";
  return (
    <div className={`rounded-2xl px-3 py-2.5 ring-1 ${tone}`}>
      <p className={`text-[9px] uppercase tracking-[0.22em] font-semibold ${labelTone}`}>{label}</p>
      <p className={`text-xl md:text-2xl font-extrabold tabular-nums leading-none mt-1.5 ${valueTone}`}>{value}</p>
    </div>
  );
}
